import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import youtubeIcon from '../assets/youtube_icon.png';
import tiktokIcon from '../assets/tiktok_icon.png';
import instagramIcon from '../assets/insta_icon.png';
import {
  selectNotifications,
  selectUnreadCount,
  selectNotificationsStatus,
  fetchNotifications,
  markAllRead,
} from '../features/notifications/notificationsSlice';
import './NotificationsPage.css';

// Facebook icon as inline SVG data URI (no PNG asset available)
const facebookIcon = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>')}`;

function NotificationsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const notificationsStatus = useSelector(selectNotificationsStatus);

  useEffect(() => {
    dispatch(fetchNotifications({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (unreadCount > 0) {
      dispatch(markAllRead());
    }
  }, [unreadCount, dispatch]);

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getPlatformIcon = (platform) => {
    const platformLower = platform?.toLowerCase();
    if (platformLower === 'youtube' || platformLower === 'youtube_advertiser_friendly') return youtubeIcon;
    if (platformLower === 'tiktok' || platformLower === 'tiktok_fyf') return tiktokIcon;
    if (platformLower === 'instagram') return instagramIcon;
    if (platformLower === 'facebook') return facebookIcon;
    return youtubeIcon;
  };

  const getGuidelinesPath = (platform) => {
    const platformLower = platform?.toLowerCase();
    if (platformLower === 'youtube') return '/guides/youtube-guidelines';
    if (platformLower === 'youtube_advertiser_friendly') return '/guides/youtube-ad-revenue-compliance';
    if (platformLower === 'tiktok') return '/guides/tiktok-guidelines';
    if (platformLower === 'tiktok_fyf') return '/guides/tiktok-fyf-eligibility';
    if (platformLower === 'instagram') return '/guides/ai-content-guidelines';
    if (platformLower === 'facebook') return '/guides/ai-content-guidelines';
    return '/articles';
  };

  const handleNotificationClick = (platform) => {
    navigate(getGuidelinesPath(platform));
  };

  return (
    <section className="notifications-page">
      <div className="notifications-page-header">
        <h1>Notifications</h1>
      </div>

      {notificationsStatus === 'loading' && notifications.length === 0 ? (
        <div className="notifications-empty">Loading notifications...</div>
      ) : notifications.length === 0 ? (
        <div className="notifications-empty">No notifications yet</div>
      ) : (
        <div className="notifications-list">
          {notifications.map((n) => (
            <article
              key={n.notification_id}
              className={`notifications-item ${!n.is_read ? 'unread' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => handleNotificationClick(n.platform)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleNotificationClick(n.platform);
                }
              }}
            >
              <div className="notifications-item-icon-column">
                <img
                  src={getPlatformIcon(n.platform)}
                  alt={n.platform}
                  className="notifications-item-icon"
                />
              </div>
              <div className="notifications-item-summary-column">
                {n.change_summary}
              </div>
              <div className="notifications-item-time-column">
                {formatRelativeTime(n.created_on)}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default NotificationsPage;
